import { parse } from "qs";
import { debounce, get } from "lodash";
import { Inject, Injectable, Optional } from "@angular/core";
import { BehaviorSubject, filter, pairwise } from "rxjs";
import { NavigationEnd, NavigationStart, Router, RoutesRecognized } from "@angular/router";

import { STRING } from "../constants/string";
import { StateService } from "./state.service";
import { decomposeUrl } from "../utils/decomposeUrl";
import { AppLocalizationRoute } from "../types.local";
import { getPathOfRoute } from "../utils/getPathOfRoute";
import { TranslatesApiService } from "./translates-api.service";
import { MED_LOCALIZATION_MODE } from "../constants/MODES.enum";
import { compareLocationPathNames } from "../utils/compareLocationPathNames";
import { MedLocales, MedLocalizationFilePath, MedLocalizationSetupConfig } from "../types";

const DELTA = 1;
const TIMEOUT = 100;

export const MED_LOCALIZATION_MODE_KEY = "MED_LOCALIZATION_MODE";

@Injectable({
  providedIn: "root",
})
export class MedLocalizationService {
  constructor(
    private router: Router,
    private state: StateService,
    private api: TranslatesApiService,
    @Optional() @Inject(MED_LOCALIZATION_MODE_KEY) private mode?: MED_LOCALIZATION_MODE,
  ) {}

  private _previousUrl = "";

  private _onLoad = debounce(() => this.state.filesList.forEach((path, index) => {
    this.api.getData(path).subscribe(result => {
      this.state.setTranslations(path, result);

      if (this.state.filesList.length === index + DELTA) {
        this.state.setLoading(false);
      }
    });
  }), TIMEOUT);

  get loading$(): BehaviorSubject<boolean> {
    return this.state.loading$;
  }

  get isDisableRoutingWatcher(): boolean {
    return this.mode === MED_LOCALIZATION_MODE.DISABLE_WATCH_ROUTING;
  }

  get locale(): keyof MedLocales {
    return this.state.locale;
  }

  set locale(value: keyof MedLocales) {
    if (value === this.state.locale || !this.state.isTagLocale(value as string)) {
      return;
    }

    this.state.locale = value;

    if (this.isDisableRoutingWatcher) {
      return;
    }

    this._onRouteChange(this.router.url);
    this._onUpdateTranslates();
  }

  init(config: MedLocalizationSetupConfig): void {
    this.state.init(config);

    if (this.isDisableRoutingWatcher) {
      return;
    }

    this.router.events
      .pipe(filter((evt: any) => evt instanceof RoutesRecognized), pairwise())
      .subscribe(([{ urlAfterRedirects }]: RoutesRecognized[]) => this._previousUrl = urlAfterRedirects);

    this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe(event => {
      const { url } = event as NavigationStart;
      this._onRouteChange(url);
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(e => {
      const isNotChangedPathname = compareLocationPathNames(this._previousUrl, (e as NavigationEnd).url);
      const { routerState: { snapshot: { root } } } = this.router;

      if (root === null || isNotChangedPathname) {
        return;
      }

      const list = getPathOfRoute(root)
        .map(({ data }: AppLocalizationRoute) => data.localizationFiles)
        .filter(Boolean)
        .flat();

      this.setFilesList(list as MedLocalizationFilePath[]);
    });
  }

  translate<R>(path: string, defaultValue?: R): R {
    return get(this.state.translateList, path) || defaultValue || path;
  }

  setFilesList(list: MedLocalizationFilePath[]): void {
    this.state.filesList = list;
    this._onUpdateTranslates();
  }

  private async _onRouteChange(url: string): Promise<void> {
    const [urlLocale, urls] = decomposeUrl(url);
    const lastUrlPath = urls.pop() || "";
    const [lastUrl, query] = lastUrlPath.split(STRING.QUESTION);
    const queryParams = parse(query) as Record<string, string | string[]>;
    const { locale } = this.state;

    if (urlLocale !== locale && !this.isDisableRoutingWatcher) {
      await this.router.navigate([locale, urls, lastUrl].flat().filter(Boolean), {
        queryParams,
      });
    }
  }

  private _onUpdateTranslates() {
    this.state.setLoading(true);

    if (this.state.isIgnoreLocale || !this.state.filesList.length) {
      return this.state.setLoading(false);
    }

    this._onLoad();
  }
}
